/**
 * The docked station screen (spec §7.3): header (station, faction, standing, wallet, lease, collections, cargo,
 * storage banner), the tab bar, the tab, notices, the inline refusal line, and the Launch footer.
 *
 * CL-6 (core-loop plan §4): the bar shows `tabGroups.primary`, then a "More ▾ (n)" button over a drawer that lists
 * the other tabs under their group headings, a grey reason line under any tab that isn't usable yet. Every tab
 * stays openable and keeps its `tab-<id>` test id (the drawer stays in the DOM, hidden while closed); each tab
 * button carries `data-group` (`bar` or its drawer group). A tab promoted this session shows a "new" dot.
 */
import { useContext, useLayoutEffect, useReducer, useRef, type ReactNode } from 'react'
import type { CommandError } from '../../career/result'
import { StateBanner } from '../charter/StateBanner'
import type { UiNotice } from '../contracts'
import { externalButtons, type ExternalTab } from '../external'
import { FocusButton, FocusContext, Meter, focusOnClick, useFocusable } from '../focus'
import { G } from '../models/layout'
import { focusKey } from '../nav'
import { LOADING_LABEL } from '../models/station'
import { STATION_TABS_CONFIG, moreSlot, moreTabs, type TabEntry } from '../models/stationTabs'
import { activeTabId, type StationView } from '../models/view'
import type { ScreenProps } from './common'
import { ContractsTab, SavesTab } from './Contracts'
import { MarketTab } from './Market'
import { OutfittingTab } from './Outfitting'
import { LeaseTab, RepairTab } from './Services'
import '../station-tabs.css'

function Header(p: { view: StationView; loading: boolean }): ReactNode {
  const h = p.view.header
  const tone = h.collections.severity
  return (
    <header className="ui-head">
      <div className="ui-head__where">
        <h1 className="ui-head__station">{h.stationName}</h1>
        <p className="ui-head__faction">
          {h.factionName} {'·'} standing <span className="ui-num">{h.standingLabel}</span>
        </p>
        {h.deskStaff !== null ? (
          <p className="ui-seed" data-testid="station-desk-staff">
            {h.deskStaff}
          </p>
        ) : null}
        {h.plate !== null ? (
          <p className="ui-seed" data-testid="station-plate">
            {h.plate}
          </p>
        ) : null}
      </div>
      <dl className="ui-head__stats">
        <div className="ui-stat">
          <dt>Wallet</dt>
          <dd className="ui-num" data-testid="station-wallet">{h.wallet}</dd>
        </div>
        <div className="ui-stat">
          <dt>Lease</dt>
          <dd className="ui-num">{h.principal}</dd>
        </div>
        <div className="ui-stat">
          <dt>Next due</dt>
          <dd>{h.nextDue}</dd>
        </div>
        <div className="ui-stat" data-tone={tone}>
          <dt>Collections</dt>
          <dd>
            {tone === 'ok' ? '✓' : '⚠'} {h.collections.label}
          </dd>
        </div>
        {h.debt !== null ? (
          <div className="ui-stat" data-tone="warning">
            <dt>Insurer debt</dt>
            <dd className="ui-num">{h.debt}</dd>
          </div>
        ) : null}
        <div className="ui-stat">
          <dt>Cargo</dt>
          <dd>
            <Meter value={h.cargoFraction} label={h.cargoLabel} tone={h.cargoFraction > 0.95 ? 'warning' : 'ok'} />
          </dd>
        </div>
      </dl>
      {h.storageBanner !== null ? (
        <p className="ui-banner" role="status">
          {'⚠'} {h.storageBanner}
        </p>
      ) : null}
      {p.loading ? (
        <p className="ui-banner ui-banner--loading" role="status" data-testid="station-loading">
          {LOADING_LABEL}
        </p>
      ) : null}
      <StateBanner stationId={p.view.station.id} />
    </header>
  )
}

function Notices(p: ScreenProps & { notices: readonly UiNotice[] }): ReactNode {
  if (p.notices.length === 0) return null
  return (
    <ul className="ui-notices" aria-live="polite">
      {p.notices.map((n, i) => (
        <li key={n.id} className="ui-notice" data-kind={n.kind}>
          <FocusButton group={G.notices} index={i} className="ui-notice__btn" testId={`notice-${n.id}`} title="Dismiss" onActivate={() => p.ctl.run(() => p.ctl.actions.dismissNotice(n.id))}>
            {n.speaker !== undefined ? <span className="ui-notice__speaker">{n.speaker}:</span> : null}
            <span className="ui-notice__text">{n.text}</span>
            <span className="ui-notice__x" aria-hidden="true">
              {'×'}
            </span>
          </FocusButton>
        </li>
      ))}
    </ul>
  )
}

function TabBody(p: ScreenProps & { view: StationView; focusedOffer: number }): ReactNode {
  const v = p.view
  switch (v.tab) {
    case 'market':
      return v.market !== null ? <MarketTab ctl={p.ctl} model={v.market} /> : null
    case 'outfitting':
      return v.outfitting !== null ? <OutfittingTab ctl={p.ctl} model={v.outfitting.model} slot={v.outfitting.slot} offers={v.outfitting.offers} focusedOffer={p.focusedOffer} /> : null
    case 'repair':
      return v.repair !== null ? <RepairTab ctl={p.ctl} model={v.repair} /> : null
    case 'lease':
      return v.lease !== null ? <LeaseTab ctl={p.ctl} model={v.lease} /> : null
    case 'contracts':
      return v.contracts !== null ? <ContractsTab ctl={p.ctl} model={v.contracts} /> : null
    case 'saves':
      return v.saves !== null ? <SavesTab ctl={p.ctl} model={v.saves} /> : null
  }
}

/** The station screen's external tabs (M3 R22f): the mountable tabs and the App's selection callbacks. */
export interface ExternalScreenProps {
  tabs: readonly ExternalTab[]
  /** Shows external tab `id`, or M2's own tab again with null. */
  select(id: string | null): void
  /** The active tab mounted `n` buttons. */
  buttons(n: number): void
}

/**
 * Mounts an external tab and puts its buttons into the focus model (`G.external`, DOM order): after every render
 * they are registered again, marked when focused (`data-focused`, `focused`) and focused on hover.
 */
function ExternalBody(p: { tab: ExternalTab; stationId: string; select: (id: string | null) => void; buttons: (n: number) => void }): ReactNode {
  const ref = useRef<HTMLDivElement>(null)
  const api = useContext(FocusContext)
  const [, refresh] = useReducer((n: number) => n + 1, 0)
  const latest = useRef(p)
  latest.current = p
  useLayoutEffect(() => {
    const el = ref.current
    if (el === null) return undefined
    return latest.current.tab.mount(el, { refresh, close: () => latest.current.select(null) })
  }, [p.tab.id, p.stationId])
  useLayoutEffect(() => {
    const el = ref.current
    if (el === null || api === null) return undefined
    const buttons = externalButtons(el)
    latest.current.buttons(buttons.length)
    const offs = buttons.map((b, i) => {
      const key = focusKey(G.external, i)
      const focused = api.focused === key
      b.dataset.nav = key
      b.classList.toggle('focused', focused)
      if (focused) b.dataset.focused = ''
      else delete b.dataset.focused
      b.onmouseenter = (): void => api.hover(G.external, i)
      return api.register(key, {
        activate: () => {
          if (!b.disabled) b.click()
        },
      })
    })
    return () => {
      for (const off of offs) off()
    }
  })
  return <div ref={ref} className="ui-external" data-testid={`external-${p.tab.id}`} />
}

/** CL-6: the station's More drawer and the session's "new" dots (UI-local, from the App). */
export interface MoreDrawerProps {
  open: boolean
  /** Opens the drawer, or closes it when open. */
  toggle(): void
  /** Shows tab `id` (`market`, `ext-charts`) and closes the drawer. */
  openTab(id: string): void
  /** Tabs promoted to the bar this session and not opened yet. */
  dots: ReadonlySet<string>
  /** Notable Wire items not yet read. */
  unread: number
}

const LABELS = STATION_TABS_CONFIG.labels

/** One tab button, on the bar or in the drawer: `tab-<id>`, `data-group`, the active mark and the "new" dot. */
function TabButton(p: { tab: TabEntry; group: string; focusGroup: string; index: number; active: boolean; dot: boolean; onOpen: (id: string) => void }): ReactNode {
  const api = useContext(FocusContext)
  const open = (): void => p.onOpen(p.tab.id)
  const f = useFocusable(p.focusGroup, p.index, { activate: open })
  return (
    <button
      type="button"
      tabIndex={-1}
      className={`ui-btn ui-tab-btn${p.active ? ' is-active' : ''}${p.tab.usable ? '' : ' is-waiting'}`}
      data-testid={`tab-${p.tab.id}`}
      data-group={p.group}
      onClick={focusOnClick(api, p.focusGroup, p.index, open)}
      {...f.props}
    >
      {p.tab.title}
      {p.dot ? (
        <span className="ui-tab-dot" data-testid={`tab-new-${p.tab.id}`} title={LABELS.newDot}>
          <span className="ui-tab-dot__label">{LABELS.newDot}</span>
        </span>
      ) : null}
    </button>
  )
}

/** CL-6: the bar (primary tabs, then More ▾ (n)) and the grouped drawer. */
function TabBar(p: { view: StationView; more: MoreDrawerProps }): ReactNode {
  const g = p.view.tabGroups
  const activeId = activeTabId(p.view)
  const slot = moreSlot(g)
  const drawer = moreTabs(g)
  const inDrawer = drawer.some((t) => t.id === activeId)
  let index = 0
  return (
    <nav className="ui-tabs ui-tabs--bar" role="tablist">
      {g.primary.map((t, i) => (
        <TabButton key={t.id} tab={t} group="bar" focusGroup={G.tabs} index={i} active={t.id === activeId} dot={p.more.dots.has(t.id)} onOpen={p.more.openTab} />
      ))}
      {slot >= 0 ? (
        <FocusButton
          group={G.tabs}
          index={slot}
          testId="station-more"
          className={`ui-tab-more${p.more.open ? ' is-open' : ''}${inDrawer ? ' is-active' : ''}`}
          title={p.more.unread > 0 ? `${p.more.unread} ${LABELS.wireUnread}` : undefined}
          onActivate={p.more.toggle}
        >
          {LABELS.more} {LABELS.moreArrow} <span className="ui-num">({drawer.length})</span>
          {p.more.unread > 0 ? (
            <span className="ui-tab-more__unread" data-testid="station-more-unread">
              {p.more.unread}
            </span>
          ) : null}
        </FocusButton>
      ) : null}
      <span className="ui-tabs__keys" aria-hidden="true">
        <kbd>Q</kbd>/<kbd>E</kbd> or <kbd>LB</kbd>/<kbd>RB</kbd>
      </span>
      {slot >= 0 ? (
        <div className="ui-more" data-testid="station-more-drawer" hidden={!p.more.open} aria-label={LABELS.drawerTitle}>
          <p className="ui-more__title">
            {LABELS.drawerTitle}
            <span className="ui-more__hint">{LABELS.closeHint}</span>
          </p>
          {g.more.map((grp) => (
            <section key={grp.group} className="ui-more__group" data-testid={`more-group-${grp.group}`}>
              <h3 className="ui-more__heading">{grp.title}</h3>
              <ul className="ui-more__list">
                {grp.tabs.map((t) => {
                  const i = index++
                  return (
                    <li key={t.id} className="ui-more__item">
                      <TabButton tab={t} group={grp.group} focusGroup={G.more} index={i} active={t.id === activeId} dot={false} onOpen={p.more.openTab} />
                      {t.reason !== null ? (
                        <p className="ui-more__reason" data-testid={`tab-reason-${t.id}`}>
                          {t.reason}
                        </p>
                      ) : null}
                    </li>
                  )
                })}
              </ul>
            </section>
          ))}
        </div>
      ) : null}
    </nav>
  )
}

/** Without a drawer host (a bare screen in tests): the drawer stays closed, with no dots or Wire count. */
const NO_MORE: MoreDrawerProps = Object.freeze({ open: false, toggle: () => undefined, openTab: () => undefined, dots: new Set<string>(), unread: 0 })

export function StationScreen(
  p: ScreenProps & {
    view: StationView
    notices: readonly UiNotice[]
    error: CommandError | null
    focusedOffer: number
    external?: ExternalScreenProps
    loading?: boolean
    more?: MoreDrawerProps
  },
): ReactNode {
  const { view, ctl } = p
  const ext = p.external
  const active = view.externalTab === null ? undefined : ext?.tabs.find((t) => t.id === view.externalTab)
  return (
    <div className="ui-station ui-panel" data-testid="station-screen" data-station={view.station.id} data-active-tab={activeTabId(view)}>
      <Header view={view} loading={p.loading === true} />
      <TabBar view={view} more={p.more ?? NO_MORE} />
      <main className="ui-body">
        {active !== undefined && ext !== undefined ? (
          <ExternalBody tab={active} stationId={view.station.id} select={ext.select} buttons={ext.buttons} />
        ) : (
          <TabBody ctl={ctl} view={view} focusedOffer={p.focusedOffer} />
        )}
      </main>
      <Notices ctl={ctl} notices={p.notices} />
      <footer className="ui-foot">
        <p className="ui-error" role="alert">
          {p.error !== null ? `⚠ ${p.error.message}` : ''}
        </p>
        <FocusButton group={G.footer} index={0} testId="launch" className="ui-launch" disabled={!view.launch.enabled} onActivate={() => ctl.launch()}>
          <span>{view.launch.label}</span>
          <span className="ui-launch__keys" aria-hidden="true">
            <kbd>L</kbd> / <kbd>Y</kbd>
          </span>
        </FocusButton>
      </footer>
    </div>
  )
}
